import React, { useEffect, useMemo, useRef, useState, startTransition } from 'react';
import Table from '../../components/Table/Table';
import { createColumnHelper } from '@tanstack/react-table';
import Dropdown from '../../components/DropDown/DropDown';
import { Col, convertToKRW, getItemWithExpiration, Row } from '../../lib/utils';
import { DemiLightText, MediumText, RegularText } from '../../lib/Text';
import SvgIcon from '../../components/SvgIcon';
import dayjs, { Dayjs } from 'dayjs';
import { colors } from '../../lib/colors';
import * as XLSX from 'xlsx'; // 엑셀 변환 라이브러리
import { saveAs } from 'file-saver'; // 파일 저장 라이브러리
import MemberSearchTextInput from '../../components/Button/MemberSearchTextInput';
import {
    ClassFeeSettlementScheduleRecord,
    FetchGetClassFeeSettlementResponse,
    GetClassFeeSettlementSummaryData,
    GetClassFeeSettlementTableData,
    GetClassFeeSettlementTableDataForMembership,
    MembershipInfo,
} from '../../services/useMemberQueryService';
import { GroupSessionMemberDetail, MemberMembershipDataType } from '../../services/useMembershipQueryService';
import NewCalendar from '../../components/Calendar/NewCalendar';
import { TooltipIcon } from './SettlementCalendar';
import { useCenterSessionCommissionTables } from '../../features/centerSessionCommissionTable/hooks';
import { formatSessionText, resolveMembershipName } from '../../lib/membershipDisplay';
import { resolveSettlementMembershipBadge, resolveSettlementMembershipStatusText } from './settlementMembershipStatus';
import SettlementLoadError from './components/SettlementLoadError';
import { formatStoredPercent, withMultiplier, withRowMultiplier } from './settlementCommissionFormat';
import {
    buildCategoryNameMap,
    formatAppliedRuleLabel,
    formatExceptionValueSource,
    withExceptionValueSource,
    formatInOutCellText,
    hasExceptionRuleRows,
    inOutForcedLabel,
    isExceptionAppliedRule,
    useSettlementCategories,
    withGroupForcedIn,
    forcedInTooltip,
} from '../../features/settlementClassification';

// 더미 출석여부
const dummyAttendanceStatusData = [
    {
        code: 'NOSHOW',
        value: '노쇼',
    },
    {
        code: 'ATTENDANCE',
        value: '출석',
    },
];

const columnHelper = createColumnHelper<GetClassFeeSettlementTableDataForMembership>();
const columnHelper2 = createColumnHelper<MemberMembershipDataType>();

type MembershipOption = {
    code: string;
    value: string;
};

type MembershipFilterRow = {
    membershipTemplateId?: string;
    membershipName?: string;
    ticketName?: string;
    name?: string;
    membership?: {
        name?: string;
        membershipName?: string;
        ticketName?: string;
    };
    membershipInfo?: {
        membership?: {
            name?: string;
        };
    };
    scheduleRecord?: {
        membershipName?: string;
    };
    members?: Array<{
        membershipTemplateId?: string;
        membershipName?: string;
        ticketName?: string;
        name?: string;
        membership?: {
            name?: string;
            membershipName?: string;
            ticketName?: string;
        };
    }>;
};

const normalizeSettlementRow = (row: any): any => {
    const membershipName = resolveMembershipName(row);
    const members = Array.isArray(row?.members) ? row.members.map(normalizeSettlementRow) : row?.members;
    const scheduleRecord = row?.scheduleRecord
        ? { ...row.scheduleRecord, membershipName: resolveMembershipName(row.scheduleRecord) || membershipName }
        : row?.scheduleRecord;

    return {
        ...row,
        membershipName,
        members,
        scheduleRecord,
    };
};

const buildMembershipTemplateOptions = (
    filterList?: Array<{ membershipTemplateId: string; membershipTemplateName: string }>,
    ...tableRowGroups: Array<MembershipFilterRow[] | undefined>
): MembershipOption[] => {
    const optionMap = new Map<string, MembershipOption>();
    const valueToCode = new Map<string, string>();

    const addOption = (code: string, value: string) => {
        const trimmedValue = value?.trim();
        if (!code || !trimmedValue) return;

        const existingCode = valueToCode.get(trimmedValue);
        if (existingCode) {
            return;
        }

        optionMap.set(code, { code, value: trimmedValue });
        valueToCode.set(trimmedValue, code);
    };

    (filterList ?? []).forEach((item) => {
        if (!item.membershipTemplateId || !item.membershipTemplateName) return;
        addOption(`id:${item.membershipTemplateId}`, item.membershipTemplateName);
    });

    tableRowGroups.forEach((rows) => {
        (rows ?? []).forEach((row) => {
            const membershipTemplateId = row.membershipTemplateId?.trim();
            const membershipName = resolveMembershipName(row);

            if (membershipTemplateId) {
                addOption(`id:${membershipTemplateId}`, membershipName || membershipTemplateId);
            } else if (membershipName) {
                addOption(`name:${membershipName}`, membershipName);
            }

            (row.members ?? []).forEach((member) => {
                const memberMembershipTemplateId = member.membershipTemplateId?.trim();
                const memberMembershipName = resolveMembershipName(member);

                if (memberMembershipTemplateId) {
                    addOption(`id:${memberMembershipTemplateId}`, memberMembershipName || memberMembershipTemplateId);
                } else if (memberMembershipName) {
                    addOption(`name:${memberMembershipName}`, memberMembershipName);
                }
            });
        });
    });

    return Array.from(optionMap.values());
};

const isMatchingMembershipTemplate = (
    row: MembershipFilterRow,
    selectedOption: string,
    options: MembershipOption[],
) => {
    if (!selectedOption || selectedOption === 'all') return true;

    const selectedValue = options.find((option) => option.code === selectedOption)?.value;
    const selectedId = selectedOption.startsWith('id:') ? selectedOption.slice(3) : undefined;
    const selectedName = selectedOption.startsWith('name:') ? selectedOption.slice(5) : selectedValue;

    const matches = (item: { membershipTemplateId?: string; membershipName?: string }) => {
        const membershipName = resolveMembershipName(item);
        if (selectedId && item.membershipTemplateId === selectedId) return true;
        if (selectedName && membershipName === selectedName) return true;
        if (selectedValue && membershipName === selectedValue) return true;
        return item.membershipTemplateId === selectedOption;
    };

    if (matches(row)) return true;
    return (row.members ?? []).some(matches);
};

const buildMembershipSearchOptions = (memberData: MembershipOption[] = [], membershipData: MembershipOption[] = []) => {
    const optionMap = new Map<string, MembershipOption>();

    memberData.forEach((member) => {
        if (!member.value) return;
        optionMap.set(`member:${member.code}`, {
            code: `member:${member.code}`,
            value: member.value,
        });
    });

    membershipData.forEach((membership) => {
        if (!membership.value) return;
        optionMap.set(`membership:${membership.code}`, {
            code: `membership:${membership.code}`,
            value: membership.value,
        });
    });

    return Array.from(optionMap.values());
};

const formatPercent = (value: number) => {
    const rounded = Math.round(Number(value) * 10) / 10;
    return `${Number.isInteger(rounded) ? rounded : rounded.toFixed(1)}%`;
};

const formatSettlementCommission = ({
    commission,
    commissionType,
    isCompositeRate,
    effectiveCommissionRate,
}: {
    commission?: number | null;
    commissionType?: string;
    isCompositeRate?: boolean;
    effectiveCommissionRate?: number | null;
}) => {
    if (effectiveCommissionRate !== undefined && effectiveCommissionRate !== null) {
        return formatPercent(effectiveCommissionRate);
    }
    if (isCompositeRate) return '복합';
    if (commission !== undefined && commission !== null) {
        return commissionType === 'fixed' ? convertToKRW(commission) : formatStoredPercent(commission);
    }
    return '-';
};

type Props = {
    date: string;
    onMonthNavigate: (date: Date | string | Dayjs) => void;
    classSettlementData: FetchGetClassFeeSettlementResponse;

    // 강사 선택
    selectedCoach: string;
    onCoachSelect: (coachId: string) => void;

    // 회원별 -> 수업기록 클릭
    onMemberScheduleRecordClick: (scheduleRecord: ClassFeeSettlementScheduleRecord) => void;
    // 회원별 -> 수강권 정보 클릭
    onMemberMembershipInfoClick: (membershipInfo: MembershipInfo) => void;
    groupType: 'class' | 'member';
    onGroupTypeChange: (type: 'class' | 'member') => void;
    selectedDate?: Dayjs | string | undefined;
    onChangeSelectedDate: (date?: Dayjs | string | undefined) => void;
    totalRemainingScheduleSession: number;
    onCommissionClick: (row: GetClassFeeSettlementTableDataForMembership) => void;
    includeGroupClassInSettlement?: boolean;
    /** 정산 숫자를 불러오지 못했을 때(예: 503 재등록 수업료 조회 실패) 표·합계 대신 보일 문구 */
    loadErrorMessage?: string;
    onRetry?: () => void;
};

const ClassPaySettlementPage = ({
    date,
    onMonthNavigate,
    classSettlementData,
    selectedCoach,
    onCoachSelect,
    onMemberScheduleRecordClick,
    onMemberMembershipInfoClick,
    groupType,
    onGroupTypeChange,
    selectedDate,
    onChangeSelectedDate,
    totalRemainingScheduleSession,
    onCommissionClick,
    includeGroupClassInSettlement = false,
    loadErrorMessage,
    onRetry,
}: Props) => {
    const authObject = getItemWithExpiration('authObject');
    const isSessionTier = authObject?.settlementType === 'sessionTier';
    /**
     * 수업별 「적용 규칙」 열 — 지금 행에 분류·재등록 예외가 하나라도 있을 때만(없으면 바꾸기 전 화면·엑셀 그대로).
     * 예외 수업료는 세 정산 방식 모두에서 계산되므로 방식은 보지 않는다(기본·세션별 정산은 예외 행에만 appliedRule 이 온다).
     * 배수 규칙 행의 commission 은 원래 요율이라, 정산 비율 칸에 서버 exceptionMultiplier 를 함께 붙인다.
     */
    const showAppliedRule = hasExceptionRuleRows(classSettlementData?.tableDataBySchedule as any);
    const { data: settlementCategoriesState } = useSettlementCategories({
        centerId: authObject?.centerId ?? '',
        enabled: showAppliedRule,
    });
    const categoryNameMap = useMemo(
        () => buildCategoryNameMap(settlementCategoriesState?.categories),
        [settlementCategoriesState?.categories],
    );
    const isCurrentSettlementMonth = dayjs(date).isSame(dayjs(), 'month');
    const classSettlementBasisText = isCurrentSettlementMonth
        ? `${dayjs(date).format('YYYY년 MM월')} 현재 시점(${dayjs().format('MM.DD HH:mm')})까지`
        : `${dayjs(date).format('YYYY년 MM월')} 월 전체`;
    const groupSettlementLabel = includeGroupClassInSettlement ? '그룹수업 합산 ON' : '그룹수업 합산 OFF';
    const getSettlementBasisNoticeRows = (isVatExclusive: boolean, vatRate: number) => [
        ['정산 기준', `${classSettlementBasisText} 완료/노쇼 수업 기준`],
        ['엑셀 기준', '현재 화면의 필터가 적용된 데이터 기준'],
        ['그룹수업 합산', groupSettlementLabel],
        ...(isVatExclusive ? [['부가세', `부가세 미포함 기준 정산 (VAT ${vatRate}% 제외)`]] : []),
    ];
    const sessionTierMonth = dayjs(date).month() + 1 < 10
        ? `0${dayjs(date).month() + 1}`
        : `${dayjs(date).month() + 1}`;
    const { data: sessionTableData } = useCenterSessionCommissionTables({
        centerId: authObject?.centerId || '',
        year: dayjs(date).year().toString(),
        month: sessionTierMonth,
        enabled: isSessionTier && !!authObject?.centerId,
    });
    const coachesMissingBrackets = React.useMemo(() => {
        if (!isSessionTier || !sessionTableData?.coachSessionCommissionTables) return [];
        return sessionTableData.coachSessionCommissionTables.filter(
            (c) => !c.scheduleBrackets || c.scheduleBrackets.length === 0,
        );
    }, [isSessionTier, sessionTableData]);

    const [position, setPosition] = useState({ top: 0, left: 0 }); // 위치 상태
    const colRef = React.useRef<HTMLDivElement>(null);
    // 툴팁 호버했을때
    const [isHover, setIsHover] = useState(false);

    // 컴포넌트가 렌더링된 후 위치값 가져오기
    useEffect(() => {
        if (colRef.current) {
            const rect = colRef?.current?.getBoundingClientRect(); // 요소의 위치와 크기 가져오기
            setPosition({ top: rect.top, left: rect.left });
        }
    }, [isHover]);

    // 수업별 뷰 — 그룹 session row 펼침 상태 (scheduleId 기준)
    const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
    const toggleExpand = React.useCallback((rowId: string) => {
        setExpandedRows((prev) => {
            const next = new Set(prev);
            if (next.has(rowId)) next.delete(rowId);
            else next.add(rowId);
            return next;
        });
    }, []);
    // 수강권 정보, 발급 일자, 회원, 강사, 회당가격, 정산 비율, 완료/노쇼 수업정산액, 남은 수업수
    const columns = useMemo(
        () => [
            columnHelper.accessor('membershipName', {
                id: 'membershipName',
                header: '수강권 정보',
                cell: (info) => (
                    <RegularText
                        fontSize={16}
                        lineHeight={23}
                        letterSpacing={-0.3}
                        className="cursor-pointer underline underline-offset-4 hover:text-primary decoration-[1px]"
                        isNoColor
                        color="#3D4244"
                        onClick={() => onMemberMembershipInfoClick(info.row.original.membershipInfo)}
                    >
                        {resolveMembershipName(info.row.original) || '-'}
                    </RegularText>
                ),
                size: 226,
                enableSorting: false,
            }),
            columnHelper.accessor('createdAt', {
                id: 'createdAt',
                header: '발급일자',
                cell: (info) => dayjs(info.getValue()).format('YYYY-MM-DD'),
                size: 135,
                enableSorting: false,
            }),

            columnHelper.accessor('memberName', {
                id: 'memberName',
                header: '회원',
                cell: (info) => {
                    const isGroup = info.row.original.source === 'group';
                    return (
                        <Row className="items-center justify-center" style={{ gap: 6 }}>
                            <span className="w-[150px] shrink-0 truncate text-[16px] leading-[23px] text-[#3D4244]" title={info.getValue() ?? '-'}>
                                {info.getValue() ?? '-'}
                            </span>
                            <span
                                className="shrink-0"
                                style={{
                                    fontSize: 13,
                                    fontWeight: 600,
                                    padding: '1px 6px',
                                    borderRadius: 4,
                                    backgroundColor: isGroup ? '#DBEAFE' : '#EFF6FF',
                                    color: '#1E40AF',
                                }}
                            >
                                {isGroup ? '그룹' : 'PT'}
                            </span>
                        </Row>
                    );
                },
                size: 210,
                minSize: 210,
                enableSorting: false,
            }),
            columnHelper.accessor('coachName', {
                id: 'coachName',
                header: '강사',
                cell: (info) => info.getValue(),
                size: 131,
                enableSorting: false,
            }),
            columnHelper.accessor('pricePerSchedule', {
                id: 'pricePerSchedule',
                header: '회당가격',
                cell: (info) => convertToKRW(info.getValue()),
                size: 126,
                enableSorting: false,
            }),
            columnHelper.accessor('commission', {
                id: 'commission',
                header: () => {
                    return (
                        <Row className=" items-center" ref={colRef}>
                            <DemiLightText fontSize={16} lineHeight={23} color="#3D4244" className="mr-[4px]">
                                정산 비율
                            </DemiLightText>
                            <SvgIcon
                                name={'SvgInfoInCircle'}
                                size={16}
                                fill={'#B4BCC4'}
                                onMouseEnter={() => setIsHover(true)}
                                onMouseLeave={() => setIsHover(false)}
                            />
                        </Row>
                    );
                },
                // header: '정산 비율',
                cell: (info) => {
                    // eslint-disable-next-line react-hooks/rules-of-hooks
                    const [isHover, setIsHover] = useState(false);
                    return (
                        <Row
                            onClick={() => startTransition(() => onCommissionClick(info.row.original))}
                            className=" items-center cursor-pointer "
                            onMouseEnter={() => setIsHover(true)}
                            onMouseLeave={() => setIsHover(false)}
                        >
                            <RegularText
                                fontSize={16}
                                lineHeight={23}
                                letterSpacing={-0.4}
                                // 「40% ×1.2」(약 75px)가 잘리지 않게 고정 폭(50px)·overflow-hidden 대신 최소 폭만 둔다(짧은 값은 예전 자리 그대로).
                                className={`mr-[8px] underline underline-offset-4 decoration-[1px] self-start whitespace-nowrap min-w-[50px] ${
                                    isHover ? 'text-primary underline-primary' : ''
                                }`}
                                isNoColor
                                color="#3D4244"
                            >
                                {` ${formatSettlementCommission({
                                    commission: info.getValue(),
                                    commissionType: info.row.original.commissionType,
                                    // 복합 요율은 모드 무관하게 서버가 내려준 값을 그대로 신뢰한다.
                                    // (tier도 대표 요율을 isCompositeRate 패턴으로 내려준다)
                                    isCompositeRate: !!info.row.original.isCompositeRate,
                                    effectiveCommissionRate: info.row.original.effectiveCommissionRate,
                                })}${withRowMultiplier(info.row.original as any)}`}
                            </RegularText>
                            {info.row.original.isCustomedCommission ? (
                                <SvgIcon
                                    name="SvgMembershipTicket"
                                    size={20}
                                    fill={isHover ? colors.primary : '#B4BCC4'}
                                    className="w-[20px]"
                                />
                            ) : (
                                <Col className="w-[20px]" />
                            )}
                        </Row>
                    );
                },
                size: 163,
                enableSorting: false,
            }),
            columnHelper.accessor('attendanceScheduleCount', {
                id: 'attendanceScheduleCount',
                header: '완료/노쇼',
                cell: (info) => {
                    return (
                        <RegularText
                            fontSize={16}
                            lineHeight={23}
                            letterSpacing={-0.3}
                            className="underline underline-offset-4 cursor-pointer deco보ration-[1px] hover:text-primary "
                            isNoColor
                            color="#3D4244"
                            onClick={() => onMemberScheduleRecordClick(info.row.original.scheduleRecord)}
                        >
                            {info.row.original.noshowScheduleCount === 0
                                ? ` ${info.getValue()}`
                                : ` ${info.getValue()}/${info.row.original.noshowScheduleCount}`}
                        </RegularText>
                    );
                },

                size: 119,
                enableSorting: false,
            }),
            ...(isSessionTier
                ? [
                      columnHelper.accessor('cumulativeSessionCount' as any, {
                          id: 'cumulativeSessionCount',
                          header: '누적 총 회차',
                          cell: (info) => {
                              const row = info.row.original as any;
                              const dummy = row.cumulativeSessionCount ?? '-';
                              return dummy;
                          },
                          size: 120,
                          enableSorting: false,
                      }),
                  ]
                : []),
            columnHelper.accessor('memberTotalScheduleFee', {
                id: 'memberTotalScheduleFee',
                header: '수업 정산액',
                cell: (info) => convertToKRW(info.getValue() ?? 0),
                size: 126,
                enableSorting: false,
            }),
            columnHelper.accessor('remainSession', {
                id: 'remainSession',
                header: '남은 수업수',
                // 값은 조회 월 말 기준 잔여. 지금은 만료된 수강권이면 배지로 현재 상태를 함께 보여준다.
                cell: (info) => {
                    const badge = resolveSettlementMembershipBadge(info.row.original);
                    return (
                        <Row className="items-center justify-center gap-[6px]">
                            <span>{info.getValue()}</span>
                            {badge && (
                                <span
                                    data-testid="settlement-membership-badge"
                                    className="rounded-[4px] px-[6px] py-[1px] text-[13px] leading-[18px] whitespace-nowrap"
                                    style={{ backgroundColor: badge.bg, color: badge.color }}
                                >
                                    {badge.label}
                                </span>
                            )}
                        </Row>
                    );
                },
                size: 119,
                enableSorting: false,
            }),
        ],
        [onCommissionClick, onMemberMembershipInfoClick, onMemberScheduleRecordClick, isSessionTier],
    );
    const columns2 = useMemo(
        () => [
            columnHelper2.accessor('coachName', {
                id: 'coachName',
                header: '강사',
                cell: (info) => {
                    const isSubRow = (info.row.original as any).__isGroupSubRow === true;
                    if (isSubRow) return '';
                    return info.getValue();
                },
                size: 149,
                enableSorting: false,
            }),
            columnHelper2.accessor('memberName', {
                id: 'memberName',
                header: '회원',
                cell: (info) => {
                    const row = info.row.original as any;
                    const isSubRow = row.__isGroupSubRow === true;
                    const isGroup = row.source === 'group';
                    const hasMembers = Array.isArray(row.members) && row.members.length > 0;
                    const isExpanded = isGroup && !isSubRow && expandedRows.has(row._id);

                    if (isSubRow) {
                        return (
                            <Row className="items-center" style={{ paddingLeft: 16 }}>
                                <span className="block w-[170px] truncate text-[14px] leading-[20px] text-[#6B7280]" title={info.getValue()}>
                                    └ {info.getValue()}
                                </span>
                            </Row>
                        );
                    }

                    // 그룹 parent: "참여 인원 / 수업가능 총 인원" 형태로 간결 표시. 회원 이름은 펼쳤을 때만 노출.
                    const members = Array.isArray(row.members) ? row.members : [];
                    const attendCount =
                        row.attendanceCount ??
                        members.filter((m: any) => m?.memberStatus === 'ATTENDANCE').length;
                    // 수업가능 총 인원 = 세션 maxCapacity (백엔드 추가 전까지 memberCount로 fallback)
                    const totalCapacity = row.maxCapacity ?? row.memberCount ?? members.length;
                    const groupDisplay = totalCapacity > 0
                        ? `${attendCount}/${totalCapacity}명`
                        : '그룹수업';
                    const displayName = isGroup ? groupDisplay : (info.getValue() ?? '-');

                    return (
                        <Row className="items-center">
                            {isGroup && hasMembers ? (
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        toggleExpand(row._id);
                                    }}
                                    style={{
                                        width: 18,
                                        flexShrink: 0,
                                        height: 18,
                                        marginRight: 4,
                                        fontSize: 13,
                                        color: '#6B7280',
                                        background: 'none',
                                        border: 'none',
                                        cursor: 'pointer',
                                        padding: 0,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                    }}
                                >
                                    {isExpanded ? '▼' : '▶'}
                                </button>
                            ) : (
                                <div style={{ width: 22, flexShrink: 0 }} />
                            )}
                            <span className="w-[150px] shrink-0 truncate text-[16px] leading-[24px] text-[#3D4244]" title={displayName}>
                                {displayName}
                            </span>
                            <span className="shrink-0"
                                style={{
                                    marginLeft: 6,
                                    fontSize: 13,
                                    fontWeight: 600,
                                    padding: '1px 6px',
                                    borderRadius: 4,
                                    backgroundColor: isGroup ? '#DBEAFE' : '#EFF6FF',
                                    color: '#1E40AF',
                                }}
                            >
                                {isGroup ? '그룹' : 'PT'}
                            </span>
                        </Row>
                    );
                },
                size: 240,
                minSize: 240,
                enableSorting: false,
            }),
            ...(authObject.settlementType === 'sessionTier'
                ? []
                : [authObject.settlementType === 'base'
                ? columnHelper2.accessor('attendanceStatus', {
                      id: 'attendanceStatus',
                      header: '상태',
                      cell: (info) => {
                          const row = info.row.original as any;
                          const isSubRow = row.__isGroupSubRow === true;
                          // sub-row는 원본 status(PENALTY_CANCELLED 포함)를 별도 필드에서 읽음
                          const rawStatus = isSubRow ? row.__memberStatus : info.getValue();
                          const text =
                              rawStatus === 'ATTENDANCE'
                                  ? '출석'
                                  : rawStatus === 'PENALTY_CANCELLED'
                                    ? '패널티'
                                    : '노쇼';
                          const isAttendance = rawStatus === 'ATTENDANCE';
                          const isPenalty = rawStatus === 'PENALTY_CANCELLED';
                          const color = isAttendance ? '#E5F8F3' : isPenalty ? '#FEF3C7' : '#FFE8E9';
                          const textColor = isAttendance ? '#00A793' : isPenalty ? '#92400E' : '#FF553E';
                          return (
                              <Col
                                  className="flex items-center justify-center w-[57px] h-[32px]"
                                  style={{ backgroundColor: color, borderRadius: 5 }}
                              >
                                  <RegularText fontSize={16} lineHeight={24} color={textColor}>
                                      {text}
                                  </RegularText>
                              </Col>
                          );
                      },
                      size: 133,
                      enableSorting: false,
                  })
                : columnHelper2.accessor('inOutType', {
                      id: 'inOutType',
                      header: '상태',
                      cell: (info) => {
                          const row = info.row.original as any;
                          // 그룹 row(parent 또는 sub)는 tier 모드에서도 attendanceStatus로 표시
                          const isGroup = row.source === 'group' || row.__isGroupSubRow === true;
                          if (isGroup) {
                              const rawStatus = row.__isGroupSubRow ? row.__memberStatus : row.attendanceStatus;
                              const text =
                                  rawStatus === 'ATTENDANCE'
                                      ? '출석'
                                      : rawStatus === 'PENALTY_CANCELLED'
                                        ? '패널티'
                                        : '노쇼';
                              const isAttendance = rawStatus === 'ATTENDANCE';
                              const isPenalty = rawStatus === 'PENALTY_CANCELLED';
                              const color = isAttendance ? '#E5F8F3' : isPenalty ? '#FEF3C7' : '#FFE8E9';
                              const textColor = isAttendance ? '#00A793' : isPenalty ? '#92400E' : '#FF553E';
                              const groupBadge = (
                                  <Col
                                      className="flex items-center justify-center w-[57px] h-[32px]"
                                      style={{ backgroundColor: color, borderRadius: 5 }}
                                  >
                                      <RegularText fontSize={16} lineHeight={24} color={textColor}>
                                          {text}
                                      </RegularText>
                                  </Col>
                              );
                              // 회원 줄만 — 노쇼라서 근무 시간 밖인데 IN 으로 계산한 예약(서버 members[].inOutForcedBy)
                              if (!(row.__isGroupSubRow && row.__inOutForcedBy === 'noshow')) return groupBadge;
                              return (
                                  <Col className="items-center gap-[2px]" title={forcedInTooltip({
                                          forcedBy: 'noshow',
                                          isCustom: !!row.isCustomedCommission,
                                          zeroRate: !!row.__forcedInZeroRate,
                                      })}>
                                      {groupBadge}
                                      <span
                                          className="whitespace-nowrap text-[12px] leading-[16px]"
                                          style={{ color: row.__forcedInZeroRate ? '#B45309' : '#6B7280' }}
                                      >
                                          {row.__forcedInZeroRate ? 'IN 계산 · 0원' : 'IN 계산'}
                                      </span>
                                  </Col>
                              );
                          }
                          const text = info.getValue() === 'IN' ? 'IN' : 'OUT';
                          const isIn = info.getValue() === 'IN';
                          const color = isIn ? '#E5F8F3' : '#FFE8E9';
                          const textColor = isIn ? '#00A793' : '#FF553E';
                          const badge = (
                              <Col
                                  className="flex items-center justify-center w-[57px] h-[32px]"
                                  style={{ backgroundColor: color, borderRadius: 5 }}
                              >
                                  <RegularText fontSize={16} lineHeight={24} color={textColor}>
                                      {text}
                                  </RegularText>
                              </Col>
                          );
                          // 상세 정산 「시간 상관없이 IN」 — 근무 시간으로는 OUT 인데 IN 으로 계산한 수업에만 이유를 붙인다(서버 값 그대로).
                          const forcedLabel = isIn ? inOutForcedLabel(row.inOutForcedBy) : null;
                          if (!forcedLabel) return badge;
                          // 배지 자리는 그대로 두고 이유를 아래 작은 글씨로(옆에 두면 배지가 밀린다)
                          return (
                              <Col className="items-center gap-[2px]" title={forcedInTooltip({
                                      forcedBy: row.inOutForcedBy,
                                      appliedRule: row.appliedRule,
                                      exceptionMode: row.exceptionMode,
                                      isCustom: !!row.isCustomedCommission,
                                      zeroRate: !!row.forcedInZeroRate,
                                  })}>
                                  {badge}
                                  <span
                                      className="whitespace-nowrap text-[12px] leading-[16px]"
                                      style={{ color: row.forcedInZeroRate ? '#B45309' : '#6B7280' }}
                                  >
                                      {row.forcedInZeroRate ? `${forcedLabel} · 0원` : forcedLabel}
                                  </span>
                              </Col>
                          );
                      },
                      size: 133,
                      enableSorting: false,
                  })]),
            columnHelper2.accessor('membershipName', {
                id: 'membershipName',
                header: '수강권',
                cell: (info) => resolveMembershipName(info.row.original) || '-',
                size: 226,
                enableSorting: false,
            }),

            columnHelper2.accessor('startTime', {
                id: 'startTime',
                header: '수업 일시',
                cell: (info) => {
                    const isSubRow = (info.row.original as any).__isGroupSubRow === true;
                    if (isSubRow) return '';
                    const v = info.getValue();
                    if (!v) return '-';
                    return dayjs(v).format('YYYY-MM-DD(ddd) HH:mm');
                },
                size: 219,
                enableSorting: false,
            }),

            columnHelper2.accessor('currentSession', {
                id: 'currentSession',
                header: '회차/총 회차',
                cell: (info) => {
                    const row = info.row.original as any;
                    const isSubRow = row.__isGroupSubRow === true;
                    const isGroupParent = row.source === 'group' && !isSubRow;
                    if (isGroupParent) return '-';
                    const v = info.getValue();
                    return formatSessionText({ currentSession: v, totalSession: row.totalSession });
                },
                size: 129,
                enableSorting: false,
            }),
            ...(isSessionTier
                ? [
                      columnHelper2.accessor('cumulativeSessionCount' as any, {
                          id: 'cumulativeSessionCount',
                          header: '누적 총 회차',
                          cell: (info) => {
                              const row = info.row.original as any;
                              const isSubRow = row.__isGroupSubRow === true;
                              const isGroupParent = row.source === 'group' && !isSubRow;
                              if (isGroupParent) return '-';
                              const dummy = row.cumulativeSessionCount ?? '-';
                              return dummy;
                          },
                          size: 120,
                          enableSorting: false,
                      }),
                  ]
                : []),
            columnHelper2.accessor('memberScheduleFee', {
                id: 'memberScheduleFee',
                header: '정산액',
                cell: (info) => {
                    const value = info.getValue();
                    return value === null || value === undefined ? '-' : convertToKRW(value ?? 0);
                },
                size: 135,
                enableSorting: false,
            }),
            columnHelper2.accessor('pricePerSchedule', {
                id: 'pricePerSchedule',
                header: '수업 금액',
                cell: (info) => {
                    const value = info.getValue();
                    return value === null || value === undefined ? '-' : convertToKRW(value ?? 0);
                },
                size: 129,
                enableSorting: false,
            }),
            columnHelper2.accessor('commission', {
                id: 'commission',
                header: '정산 비율',
                cell: (info) => {
                    const row = info.row.original as any;
                    const isSubRow = row.__isGroupSubRow === true;
                    // 수업별 표는 상태 칸(IN · 예외)과 적용 규칙 칸(…IN 요율 ×1.2)이 이미 알려 주므로 배수만 붙인다(칸 폭 — 줄바꿈 방지).
                    const multiplierText = isSubRow ? '' : withMultiplier(row.exceptionMultiplier);
                    const text = ` ${formatSettlementCommission({
                        commission: info.getValue(),
                        commissionType: row.commissionType,
                        // 모드 무관. sub-row는 parent spread로 값이 새어들어오므로 계속 제외한다.
                        isCompositeRate: !isSubRow && !!row.isCompositeRate,
                        effectiveCommissionRate: isSubRow ? undefined : row.effectiveCommissionRate,
                    })}${multiplierText}`;
                    // 수강권별 커스텀 요율이 걸린 수업이면 수강권별 탭과 같은 티켓 아이콘을 붙인다.
                    // 서버가 이 필드를 안 내려주면(=기존 응답) 문자열 그대로 반환 → 렌더링 완전 동일.
                    if (!row.isCustomedCommission) return text;
                    return (
                        <Row className="items-center">
                            <span className="mr-[8px]">{text}</span>
                            <SvgIcon
                                name="SvgMembershipTicket"
                                size={20}
                                fill="#B4BCC4"
                                className="w-[20px] shrink-0"
                            />
                        </Row>
                    );
                },
                size: 134,
                enableSorting: false,
            }),
            ...(showAppliedRule
                ? [
                      columnHelper2.accessor('appliedRule' as any, {
                          id: 'appliedRule',
                          header: '적용 규칙',
                          cell: (info) => {
                              const row = info.row.original as any;
                              if (row.__isGroupSubRow === true || row.source === 'group') return '';
                              const label = formatAppliedRuleLabel(
                                  row.appliedRule,
                                  categoryNameMap,
                                  row.exceptionMultiplier,
                                  row.exceptionMode,
                              );
                              if (!label) return '-';
                              const isException = isExceptionAppliedRule(row.appliedRule);
                              // 서버 10/18 F6: 발급 기록이 없는 옛 수강권이라 지금의 신규/재등록 표시로 재등록 수업료를 정했다.
                              const isIssueKindFallback =
                                  row.appliedRule === 'reRegistration' && row.reRegistrationSource === 'isNewSalesFallback';
                              // 기본 정산: 신규 칸이 아니라 재등록·노쇼 칸 값을 썼으면 한 줄 덧붙인다(서버 exceptionValueSource).
                              const valueSourceText = isException ? formatExceptionValueSource(row.exceptionValueSource) : null;
                              return (
                                  <span
                                      className="whitespace-nowrap"
                                      // 파란 글자는 이 표에서 링크로 읽힌다 — 예외는 기본 글자색, 기본·개별은 회색.
                                      style={{ color: isException ? '#3D4244' : '#6B7280' }}
                                  >
                                      {label}
                                      {isIssueKindFallback && (
                                          <span className="block text-[13px] leading-[18px]" style={{ color: '#6B7280' }}>
                                              옛 수강권 · 현재 재등록 표시 기준
                                          </span>
                                      )}
                                      {valueSourceText && (
                                          <span className="block text-[13px] leading-[18px]" style={{ color: '#6B7280' }}>
                                              {valueSourceText}
                                          </span>
                                      )}
                                  </span>
                              );
                          },
                          size: 130,
                          enableSorting: false,
                      }),
                  ]
                : []),
        ],
        [authObject.settlementType, expandedRows, toggleExpand, isSessionTier, showAppliedRule, categoryNameMap],
    );
    const [isCalendarOpen, setIsCalendarOpen] = useState(false); // 달력 표시 여부

    // 월 요약 데이터
    const monthlySummary = useMemo(() => classSettlementData?.summaryData, [classSettlementData]);

    // 필터링 데이터
    // 강사 데이터 , coachId, coachName -> code, value
    const coachData = classSettlementData?.coachs.map((coach) => ({
        code: coach.coachId,
        value: coach.coachName,
    }));
    // 회원 검색데이터, memberId, memberName -> code, value
    const memberData = useMemo(
        () =>
            classSettlementData?.filters?.memberList.map((member) => ({
                code: member.memberId,
                value: member.memberName,
            })) ?? [],
        [classSettlementData?.filters?.memberList],
    );
    // 수강권 데이터: 서버 필터 목록에 빠진 그룹/발급 수강권명도 현재 row 기준으로 보강한다.
    const membershipTemplateData = useMemo(
        () =>
            buildMembershipTemplateOptions(
                classSettlementData?.filters?.membershiptemplateList,
                classSettlementData?.tableDataByMembership,
                classSettlementData?.tableDataBySchedule,
            ),
        [
            classSettlementData?.filters?.membershiptemplateList,
            classSettlementData?.tableDataByMembership,
            classSettlementData?.tableDataBySchedule,
        ],
    );
    const searchData = useMemo(
        () => buildMembershipSearchOptions(memberData, membershipTemplateData),
        [memberData, membershipTemplateData],
    );

    const [search, setSearch] = React.useState('');
    const [rowSelected, setRowSelected] = React.useState({});

    // 테이블 데이터
    const tableData = useMemo(() => {
        const rows =
            groupType === 'member' ? classSettlementData?.tableDataByMembership : classSettlementData?.tableDataBySchedule;
        return rows?.map(normalizeSettlementRow);
    }, [classSettlementData?.tableDataByMembership, classSettlementData?.tableDataBySchedule, groupType]);

    // const [defaultData, setDefaultData] = React.useState(tableData);
    // 로우 총합 단가수
    const [totalUnitPrice, setTotalUnitPrice] = React.useState(0);
    // 필터데이터
    const [filteredData, setFilteredData] = React.useState(tableData);

    // 숙강권 선택
    const [selectedClassTicket, setSelectedClassTicket] = React.useState('all');
    // 출석여부 선택
    const [selectedAttendanceStatus, setSelectedAttendanceStatus] = React.useState('');
    // 회원 선택
    const [selectedMember, setSelectedMember] = React.useState('');

    const handleSelect = (
        selected: { code: string; value: string },
        type: 'coach' | 'classTicket' | 'attendanceStatus',
    ) => {
        if (type === 'coach') {
            // setSelectedCoach(selected.code);
            onCoachSelect(selected.code);
        } else if (type === 'classTicket') {
            setSelectedClassTicket(selected.code);
        } else if (type === 'attendanceStatus') {
            setSelectedAttendanceStatus(selected.code);
        }
    };

    // 회원별 엑셀 다운로드
    const exportToExcelForMember = () => {
        const data: GetClassFeeSettlementTableDataForMembership[] =
            filteredData as GetClassFeeSettlementTableDataForMembership[];

        const vatPolicy = authObject?.vatPolicy ?? 'inclusive';
        const vatRate = authObject?.vatRate ?? 10;
        const isVatExclusive = vatPolicy === 'exclusive';

        const localizedData = data.map((item) => ({
            '수강권 정보': resolveMembershipName(item) || '-',
            발급일자: dayjs(item.createdAt).format('YYYY-MM-DD'),
            회원: item.memberName,
            강사: item.coachName,
            회당가격: convertToKRW(item.pricePerSchedule),
            '정산 비율': `${formatSettlementCommission({
                commission: item.commission,
                commissionType: item.commissionType,
                // 화면과 동일 기준 — 모드 무관하게 서버 isCompositeRate를 그대로 쓴다
                isCompositeRate: !!item.isCompositeRate,
                effectiveCommissionRate: item.effectiveCommissionRate,
            })}${withRowMultiplier(item as any)}`,
            '완료/노쇼': item.attendanceScheduleCount + '/' + item.noshowScheduleCount,
            ...(isSessionTier
                ? {
                      '누적 총 회차': (item as any).cumulativeSessionCount ?? '-',
                  }
                : {}),
            '수업 정산액': convertToKRW(item.memberTotalScheduleFee),
            '남은 수업수': item.remainSession,
            '수강권 상태': resolveSettlementMembershipStatusText(item),
        }));
        // 1. JSON 데이터를 Sheet로 변환
        const worksheet = XLSX.utils.json_to_sheet(localizedData);
        // 2. Workbook 생성 및 Sheet 추가
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'FilteredData');
        const noticeSheet = XLSX.utils.aoa_to_sheet(getSettlementBasisNoticeRows(isVatExclusive, vatRate));
        XLSX.utils.book_append_sheet(workbook, noticeSheet, '정산 기준');
        // 3. 엑셀 파일 생성 및 다운로드
        const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
        const blob = new Blob([excelBuffer], { type: 'application/octet-stream' });
        saveAs(blob, `${dayjs(date).format('YYYY-MM')} 수강권별 수업료 정산.xlsx`);
    };
    // 수업별 엑셀 다운로드
    // A안: 그룹 session row는 합계 row + 들여쓰기된 회원별 sub-row로 전개.
    // sub-row의 금액 컬럼은 빈 문자열 → SUM 중복 방지 (상위 합계 row만 계산됨).
    // IN/OUT 컬럼은 tier 모드에서만 포함. base/sessionTier 모드에선 제외.
    // 누적 총 회차 컬럼은 sessionTier 모드에서만 포함.
    const exportToExcelForClss = () => {
        const data: MemberMembershipDataType[] = filteredData as MemberMembershipDataType[];
        const vatPolicy = authObject?.vatPolicy ?? 'inclusive';
        const vatRate = authObject?.vatRate ?? 10;
        const isVatExclusive = vatPolicy === 'exclusive';
        const isBaseMode = authObject.settlementType === 'base';
        const showInOut = authObject.settlementType === 'tier';
        const formatCommission = (
            commission?: number | null,
            commissionType?: string,
            isCompositeRate?: boolean,
            effectiveCommissionRate?: number | null,
        ) =>
            formatSettlementCommission({
                commission,
                commissionType,
                isCompositeRate,
                effectiveCommissionRate,
            });

        const localizedData: Record<string, string | number>[] = [];
        for (const item of data) {
            const vatExcludedPrice = Math.floor((item.pricePerSchedule ?? 0) / (1 + vatRate / 100));
            if (item.source === 'group' && Array.isArray(item.members) && item.members.length > 0) {
                // 그룹 상위(합계) row
                localizedData.push({
                    구분: '그룹',
                    강사: item.coachName ?? '',
                    회원: item.memberName ?? '',
                    ...(showInOut ? { 'IN/OUT': '' } : {}),
                    '수업 일시': item.startTime ? dayjs(item.startTime).format('YYYY-MM-DD(ddd) HH:mm') : '',
                    수강권: resolveMembershipName(item) || '-',
                    '회차/총 회차': '-',
                    ...(isSessionTier ? { '누적 총 회차': '-' } : {}),
                    정산액: item.memberScheduleFee ?? 0,
                    ...(isVatExclusive ? { '수업 금액(부가세제외)': vatExcludedPrice } : {}),
                    '수업 금액': item.pricePerSchedule ?? 0,
                    '정산 비율': formatCommission(
                        item.commission,
                        item.commissionType,
                        (item as any).isCompositeRate,
                        (item as any).effectiveCommissionRate,
                    ),
                    ...(showAppliedRule ? { '적용 규칙': '' } : {}),
                });
                // 참여 회원별 sub-row (금액 컬럼 비움 → SUM 중복 방지)
                for (const m of item.members) {
                    const statusText =
                        m.attendanceStatus === 'ATTENDANCE'
                            ? '출석'
                            : m.attendanceStatus === 'PENALTY_CANCELLED'
                              ? '패널티'
                              : '노쇼';
                    localizedData.push({
                        구분: '',
                        강사: '',
                        회원: `  └ ${m.memberName} (${withGroupForcedIn(statusText, m)})`,
                        ...(showInOut ? { 'IN/OUT': '' } : {}),
                        '수업 일시': '',
                        수강권: resolveMembershipName(m) || '-',
                        '회차/총 회차': formatSessionText(m),
                        ...(isSessionTier
                            ? {
                                  '누적 총 회차': (m as any).cumulativeSessionCount ?? '-',
                              }
                            : {}),
                        정산액: '',
                        ...(isVatExclusive ? { '수업 금액(부가세제외)': '' } : {}),
                        '수업 금액': '',
                        '정산 비율': formatCommission(m.commission, m.commissionType),
                        ...(showAppliedRule ? { '적용 규칙': '' } : {}),
                    });
                }
            } else {
                // PT row (기존 동작 유지)
                localizedData.push({
                    구분: 'PT',
                    강사: item.coachName ?? '',
                    회원: item.memberName ?? '',
                    ...(showInOut ? { 'IN/OUT': formatInOutCellText(item) } : {}),
                    '수업 일시': item.startTime ? dayjs(item.startTime).format('YYYY-MM-DD(ddd) HH:mm') : '',
                    수강권: resolveMembershipName(item) || '-',
                    '회차/총 회차': formatSessionText(item),
                    ...(isSessionTier
                        ? { '누적 총 회차': (item as any).cumulativeSessionCount ?? '-' }
                        : {}),
                    정산액: item.memberScheduleFee ?? 0,
                    ...(isVatExclusive ? { '수업 금액(부가세제외)': vatExcludedPrice } : {}),
                    '수업 금액': item.pricePerSchedule ?? 0,
                    '정산 비율': `${formatCommission(
                        item.commission,
                        item.commissionType,
                        (item as any).isCompositeRate,
                        (item as any).effectiveCommissionRate,
                    )}${withRowMultiplier(item as any)}`,
                    ...(showAppliedRule
                        ? {
                              '적용 규칙': withExceptionValueSource(
                                  formatAppliedRuleLabel(
                                      (item as any).appliedRule,
                                      categoryNameMap,
                                      (item as any).exceptionMultiplier,
                                      (item as any).exceptionMode,
                                  ) ?? '-',
                                  item as any,
                              ),
                          }
                        : {}),
                });
            }
        }
        // 1. JSON 데이터를 Sheet로 변환
        const worksheet = XLSX.utils.json_to_sheet(localizedData);
        // 2. Workbook 생성 및 Sheet 추가
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'FilteredData');
        const noticeSheet = XLSX.utils.aoa_to_sheet(getSettlementBasisNoticeRows(isVatExclusive, vatRate));
        XLSX.utils.book_append_sheet(workbook, noticeSheet, '정산 기준');
        // 3. 엑셀 파일 생성 및 다운로드
        const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
        const blob = new Blob([excelBuffer], { type: 'application/octet-stream' });
        saveAs(blob, `${dayjs(date).format('YYYY-MM')} 수업별 수업료 정산.xlsx`);
    };

    // 강사, 수강권, 출석여부, 회원검색 필터링
    useEffect(() => {
        if (tableData) {
            let tempData: any = tableData;

            if (selectedCoach && selectedCoach !== 'all') {
                tempData = tempData.filter((data: any) => data.coachId === selectedCoach);
            }
            if (selectedMember && selectedMember !== 'all') {
                tempData = tempData.filter((data: any) => {
                    if (data.memberId === selectedMember) return true;
                    // 그룹 session row는 participants의 memberId 검사
                    if (data.source === 'group' && Array.isArray(data.members)) {
                        return data.members.some((m: any) => m.memberId === selectedMember);
                    }
                    return false;
                });
            }

            if (selectedClassTicket !== 'all') {
                tempData = tempData.filter((data: any) =>
                    isMatchingMembershipTemplate(data, selectedClassTicket, membershipTemplateData),
                );
            }

            // 회원/수강권 이름 검색 (수강권별/수업별 공통)
            if (search) {
                const q = search.toLowerCase();
                tempData = tempData.filter((data: any) => {
                    const name = (data.memberName ?? '').toLowerCase();
                    const membershipName = resolveMembershipName(data).toLowerCase();
                    if (name.includes(q)) return true;
                    if (membershipName.includes(q)) return true;
                    // 그룹 session row는 참여 회원 이름도 검사
                    if (data.source === 'group' && Array.isArray(data.members)) {
                        return data.members.some(
                            (m: any) =>
                                (m.memberName ?? '').toLowerCase().includes(q) ||
                                resolveMembershipName(m).toLowerCase().includes(q),
                        );
                    }
                    return false;
                });
            }

            // 수업별일때만 필터링
            if (groupType === 'class') {
                if (selectedAttendanceStatus && selectedAttendanceStatus !== 'all') {
                    tempData = tempData.filter((data: any) => data.attendanceStatus === selectedAttendanceStatus);
                }
                if (selectedDate) {
                    tempData = tempData.filter((data: any) => dayjs(data.startTime).isSame(dayjs(selectedDate), 'day'));
                }
            }
            setFilteredData(tempData);
        }
    }, [
        tableData,
        selectedCoach,
        selectedClassTicket,
        selectedAttendanceStatus,
        selectedMember,
        selectedDate,
        search,
        groupType,
        membershipTemplateData,
    ]);

    /**
     * 수업별 뷰에서 그룹 session row를 펼쳤을 때 sub-row를 inline으로 삽입한 데이터.
     * Table 컴포넌트는 자체 expanded row model을 사용하지 않으므로 데이터 레벨에서 flatten.
     * sub-row는 marker `__isGroupSubRow: true` + 유니크 `_id`로 구분.
     */
    const displayData = React.useMemo(() => {
        if (!filteredData) return [];
        if (groupType !== 'class') return filteredData;
        const out: any[] = [];
        for (const row of filteredData as any[]) {
            out.push(row);
            if (
                row?.source === 'group' &&
                Array.isArray(row?.members) &&
                row.members.length > 0 &&
                expandedRows.has(row._id)
            ) {
                for (const m of row.members as GroupSessionMemberDetail[]) {
                    out.push({
                        ...row,
                        __isGroupSubRow: true,
                        __parentId: row._id,
                        // tanstack row id 충돌 방지
                        _id: `${row._id}__sub__${m.memberId}`,
                        memberId: m.memberId,
                        memberName: m.memberName,
                        // 'PENALTY_CANCELLED'는 parent type과 호환되지 않으므로 원본 값은 별도 필드로 보관
                        __memberStatus: m.attendanceStatus,
                        // 노쇼라서 근무 시간 밖인데 IN 으로 계산한 예약(상세 정산) — 상태 칸에 「IN」을 덧붙인다
                        __inOutForcedBy: m.inOutForcedBy,
                        __forcedInZeroRate: m.forcedInZeroRate,
                        attendanceStatus:
                            m.attendanceStatus === 'PENALTY_CANCELLED' ? 'NOSHOW' : m.attendanceStatus,
                        membershipName: resolveMembershipName(m),
                        currentSession: m.currentSession,
                        totalSession: m.totalSession,
                        pricePerSchedule: m.pricePerSchedule,
                        memberScheduleFee: m.memberScheduleFee,
                        commission: m.commission,
                        commissionType: m.commissionType,
                        effectiveCommissionRate: undefined,
                        // parent spread로 새어들어오는 것을 막고 회원 본인 값만 쓴다.
                        // 서버가 member에 안 내려주면 undefined → 아이콘 미표시(기존과 동일).
                        isCustomedCommission: (m as any).isCustomedCommission,
                        cumulativeSessionCount: (m as any).cumulativeSessionCount,
                        members: undefined,
                    });
                }
            }
        }
        return out;
    }, [filteredData, expandedRows, groupType]);

    // 총 필터된 건수
    const filteredCount = filteredData?.length ?? 0;
    // 필터된 데이터의 수업료 총합
    const filteredTotalPrice =
        (groupType === 'class' &&
            filteredData &&
            filteredData.length > 0 &&
            (filteredData as any[]).reduce((acc, cur) => acc + (cur?.memberScheduleFee || 0), 0)) ||
        0;

    return (
        <div className="flex flex-col w-full h-full pt-[29px] px-[46px] bg-white rounded-[27px] mb-[50px]">
            {isSessionTier && coachesMissingBrackets.length > 0 && (
                <Row className="items-center mb-[20px] px-[16px] py-[12px] bg-[#FEF3C7] rounded-[10px] gap-[8px]">
                    <SvgIcon name="SvgWarningInCircle" size={17} fill="#92400E" />
                    <RegularText fontSize={14} lineHeight={20} color="#92400E">
                        {`${coachesMissingBrackets.map((c) => c.coachName).join(', ')} 강사의 세션 구간 설정이 필요합니다.`}
                    </RegularText>
                </Row>
            )}
            <Row className=" items-center mb-[40px] ">
                <MediumText fontSize={25} lineHeight={25} color={'#3D4244'} className="mr-[14px]">
                    {dayjs(date).format('YYYY년 MM월')}
                </MediumText>
                <div
                    className="flex cursor-pointer bg-[#F4F5F7] w-[30px] h-[30px] justify-center items-center rounded-[4px] mr-[5px]"
                    onClick={() => {
                        onMonthNavigate(dayjs(date).subtract(1, 'month'));
                    }}
                >
                    <SvgIcon
                        name={'SvgLeftStrokeArrow'}
                        size={15}
                        fill={colors.trans}
                        stroke={'#70747C'}
                        strokeWidth={1.2}
                    />
                </div>

                <div
                    className="flex cursor-pointer bg-[#F4F5F7] w-[30px] h-[30px] justify-center items-center rounded-[4px] mr-[30px]"
                    onClick={() => {
                        onMonthNavigate(dayjs(date).add(1, 'month'));
                    }}
                >
                    <SvgIcon
                        name={'SvgRightStrokeArrow'}
                        size={15}
                        fill={colors.trans}
                        stroke={'#70747C'}
                        strokeWidth={1.2}
                    />
                </div>
                <GroupButtonRow
                    groupType={groupType}
                    onGroupTypeChange={(type) => {
                        onGroupTypeChange(type);

                        setFilteredData(
                            type === 'member'
                                ? classSettlementData?.tableDataByMembership?.map(normalizeSettlementRow)
                                : classSettlementData?.tableDataBySchedule?.map(normalizeSettlementRow),
                        );
                    }}
                />
                {coachData && coachData.length > 0 && (
                    <Dropdown
                        initialSelected={selectedCoach}
                        options={coachData}
                        onSelect={(selected) => {
                            handleSelect(selected, 'coach');
                        }}
                        bgColor="rgba(201, 219, 255, 0.4)"
                        textColor={colors.primary}
                        borderRadius={50}
                        totalText="모든 강사"
                        arrowColor={colors.primary}
                        className="py-[14px]"
                        textType="medium"
                    />
                )}
            </Row>
            {loadErrorMessage ? (
                <SettlementLoadError message={loadErrorMessage} onRetry={onRetry} />
            ) : (
            <>
            {authObject?.vatPolicy === 'exclusive' && (
                <Row className="flex items-center mb-[12px] px-[12px] py-[8px] bg-[#F4F5F7] rounded-[8px]">
                    <RegularText fontSize={14} lineHeight={20} color="#6B7280" letterSpacing={-0.3}>
                        {'\u24D8 부가세 미포함 기준으로 정산된 금액입니다'}
                    </RegularText>
                </Row>
            )}
            <Col className="w-full mb-[12px] px-[14px] py-[10px] bg-[#F4F7FF] border border-solid border-[#D9E6FF] rounded-[8px]">
                <Row className="items-start justify-between gap-[10px]">
                    <Row className="items-start min-w-0 flex-1">
                        <SvgIcon
                            name="SvgInfoInCircle"
                            size={16}
                            fill={colors.primary}
                            className="mr-[7px] mt-[2px] flex-shrink-0"
                        />
                        <RegularText
                            fontSize={14}
                            lineHeight={20}
                            color="#3D4244"
                            letterSpacing={-0.3}
                            className="min-w-0 break-keep"
                        >
                            {`수업료 정산은 ${classSettlementBasisText} 완료/노쇼 수업 기준입니다. 엑셀은 현재 화면의 필터 데이터로 생성됩니다.`}
                        </RegularText>
                    </Row>
                    <Row
                        className="items-center justify-center rounded-[20px] px-[9px] py-[4px] flex-shrink-0"
                        style={{ backgroundColor: includeGroupClassInSettlement ? '#E6F8F0' : '#F2F4F6' }}
                    >
                        <RegularText
                            fontSize={13}
                            lineHeight={18}
                            color={includeGroupClassInSettlement ? '#067A46' : '#6B7280'}
                            letterSpacing={-0.2}
                        >
                            {groupSettlementLabel}
                        </RegularText>
                    </Row>
                </Row>
                {isCurrentSettlementMonth && (
                    <RegularText
                        fontSize={13}
                        lineHeight={19}
                        color="#6B7280"
                        letterSpacing={-0.25}
                        className="mt-[6px]"
                    >
                        급여 계산은 월 전체 수업(예정 포함) 기준이라 금액이 다를 수 있습니다.
                    </RegularText>
                )}
            </Col>
            <TableHeader
                summaryData={monthlySummary}
                date={date}
                totalRemainingScheduleSession={totalRemainingScheduleSession}
            />
            {groupType === 'member' ? (
                <Row className="flex w-full justify-end mb-[30px]">
                    <Dropdown
                        options={membershipTemplateData}
                        className="h-[40px] mr-[12px]"
                        totalText="모든 수강권"
                        onSelect={(selected) => {
                            handleSelect(selected, 'classTicket');
                        }}
                        selectedBgColor="#C9DBFF66"
                        selectedTextColor={colors.primary}
                        selectedArrowColor={colors.primary}
                    />
                    <Row
                        className={` items-center justify-center bg-[#F2F4F6] rounded-[7px] px-[15px] py-[5px] mr-[18px]  ${
                            filteredData && filteredData.length > 0 ? 'cursor-pointer' : 'opacity-50 cursor-not-allowed'
                        }`}
                        onClick={() => {
                            filteredData && filteredData.length > 0 && exportToExcelForMember();
                        }}
                    >
                        <SvgIcon name={'SvgExcel'} size={20} fill={colors.trans} className="mr-[7px]" />
                        <RegularText fontSize={16} lineHeight={23} color="#3D4244">
                            엑셀 다운로드
                        </RegularText>
                    </Row>
                    {searchData.length > 0 && (
                        <MemberSearchTextInput
                            data={searchData}
                            width={180}
                            placeholder="회원/수강권검색"
                            maxLength={30}
                            onSelect={(select) => setSearch(select.value)}
                            onSearch={(search) => setSearch(search)}
                        />
                    )}
                </Row>
            ) : (
                <Row className="flex w-full justify-between mb-[30px]">
                    <Row>
                        <RegularText fontSize={20} lineHeight={24} color="#3D4244">
                            {`총 ${filteredCount}건 / 총 수업료 ${convertToKRW(filteredTotalPrice)}`}
                        </RegularText>
                    </Row>
                    <Row>
                        <button
                            className="flex flex-row relative items-center justify-center rounded-[7px] px-[12px] mr-[12px] "
                            onClick={() => setIsCalendarOpen(true)}
                            style={{
                                backgroundColor: selectedDate ? '#C9DBFF66' : '#F2F4F6',
                            }}
                        >
                            <DemiLightText
                                fontSize={17}
                                lineHeight={24.62}
                                color={selectedDate ? colors.primary : '#3D4244'}
                                className="mr-[12px]"
                            >
                                {selectedDate ? dayjs(selectedDate).format('D일') : '모든 날짜'}
                            </DemiLightText>
                            <SvgIcon
                                name={'SvgDownStrokeArrow'}
                                fill={colors.trans}
                                stroke={selectedDate ? colors.primary : '#3D4244'}
                                size={14}
                                strokeWidth={1.5}
                            />
                            {isCalendarOpen && (
                                <NewCalendar
                                    date={dayjs(date)}
                                    selectedDay={selectedDate}
                                    setIsCalendar={setIsCalendarOpen}
                                    onClickDate={(day) => {
                                        onChangeSelectedDate((day && dayjs(day)) ?? undefined);
                                    }}
                                />
                            )}
                        </button>
                        <Dropdown
                            options={membershipTemplateData}
                            className="h-[40px] mr-[12px]"
                            totalText="모든 수강권"
                            onSelect={(selected) => {
                                handleSelect(selected, 'classTicket');
                            }}
                            selectedBgColor="#C9DBFF66"
                            selectedTextColor={colors.primary}
                            selectedArrowColor={colors.primary}
                        />
                        <Dropdown
                            options={dummyAttendanceStatusData}
                            className="h-[40px] mr-[12px]"
                            totalText="전체 상태"
                            onSelect={(selected) => {
                                handleSelect(selected, 'attendanceStatus');
                            }}
                            selectedBgColor="#C9DBFF66"
                            selectedTextColor={colors.primary}
                            selectedArrowColor={colors.primary}
                        />
                        <Row
                            className={` items-center justify-center bg-[#F2F4F6] rounded-[7px] px-[15px] py-[5px] mr-[18px]  ${
                                filteredData && filteredData.length > 0
                                    ? 'cursor-pointer'
                                    : 'opacity-50 cursor-not-allowed'
                            }`}
                            onClick={() => filteredData && filteredData.length > 0 && exportToExcelForClss()}
                        >
                            <SvgIcon name={'SvgExcel'} size={20} fill={colors.trans} className="mr-[7px]" />
                            <RegularText fontSize={16} lineHeight={23} color="#3D4244" letterSpacing={-0.32}>
                                엑셀 다운로드
                            </RegularText>
                        </Row>
                        <MemberSearchTextInput
                            data={searchData}
                            width={180}
                            placeholder="회원/수강권검색"
                            maxLength={30}
                            onSelect={(select) => setSearch(select.value)}
                            onSearch={(search) => setSearch(search)}
                        />
                    </Row>
                </Row>
            )}
            {filteredData && filteredData.length > 0 ? (
                <Table
                    showHorizontalScrollHint
                    data={groupType === 'class' ? displayData : filteredData}
                    columns={groupType === 'member' ? columns : columns2}
                    className="mb-[100px]"
                    width={groupType === 'class' ? 1300 : 1238}
                    isPagination
                    autoResetPageIndex={false}
                    // 수업별 뷰: 그룹 row 펼침 시 sub-row가 페이지 경계를 밀어내 원래 row가 다음 페이지로 사라지는 문제 방지.
                    // pageSize를 충분히 크게 설정해 실질적 페이징 없이 스크롤로 보게 한다.
                    pageSize={groupType === 'class' ? 9999 : 10}
                    customPaginationComponent={({
                        firstPage,
                        nextPage,
                        canNextPage,
                        previousPage,
                        canPreviousPage,
                    }) => (
                        <Row className=" absolute items-center flex right-0 bottom-0 mb-[20px]">
                            <RegularText
                                fontSize={16}
                                lineHeight={23}
                                color="#3D4244"
                                className={`mr-[16px] ${
                                    canPreviousPage ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
                                }`}
                                onClick={() => !canPreviousPage && firstPage()}
                            >
                                처음으로
                            </RegularText>
                            <button
                                className="flex flex-row items-center mr-[13px] bg-[#F4F5F7] rounded-[7px] px-[19px] py-[7px] 
                        disabled:opacity-50 disabled:cursor-not-allowed
                        "
                                onClick={previousPage}
                                disabled={canPreviousPage}
                            >
                                <SvgIcon
                                    name={'SvgLeftStrokeArrow'}
                                    size={15}
                                    fill={colors.trans}
                                    stroke={'#70747C'}
                                    strokeWidth={1.2}
                                    className="mr-[7px]"
                                />
                                <RegularText fontSize={16} lineHeight={23} color="#3D4244">
                                    이전
                                </RegularText>
                            </button>
                            <button
                                className="flex flex-row items-center mr-[13px] bg-[#F4F5F7] rounded-[7px] px-[19px] py-[7px] 
                        disabled:opacity-50 disabled:cursor-not-allowed
                        "
                                onClick={nextPage}
                                disabled={canNextPage}
                            >
                                <RegularText fontSize={16} lineHeight={23} color="#3D4244" className="mr-[7px]">
                                    다음
                                </RegularText>
                                <SvgIcon
                                    name={'SvgRightStrokeArrow'}
                                    size={15}
                                    fill={colors.trans}
                                    stroke={'#70747C'}
                                    strokeWidth={1.2}
                                />
                            </button>
                        </Row>
                    )}
                />
            ) : (
                <Col>
                    {/* {groupType === 'member' ? (
                        <Row className="flex flex-row w-full h-[44px] bg-[#EEF0F2] rounded-[9px] items-center">
                            <DemiLightText
                                fontSize={16}
                                lineHeight={24}
                                color="#3D4244"
                                className="w-[149px] text-center"
                            >
                                강사
                            </DemiLightText>
                            <DemiLightText
                                fontSize={16}
                                lineHeight={24}
                                color="#3D4244"
                                className="w-[145px] text-center"
                            >
                                회원
                            </DemiLightText>
                            <DemiLightText
                                fontSize={16}
                                lineHeight={24}
                                color="#3D4244"
                                className="w-[149px] text-center"
                            >
                                완료/노쇼
                            </DemiLightText>
                            <DemiLightText
                                fontSize={16}
                                lineHeight={24}
                                color="#3D4244"
                                className="w-[149px] text-center"
                            >
                                남은 수업 수
                            </DemiLightText>
                            <DemiLightText
                                fontSize={16}
                                lineHeight={24}
                                color="#3D4244"
                                className="w-[149px] text-center"
                            >
                                총 수업 정산액
                            </DemiLightText>
                            <DemiLightText
                                fontSize={16}
                                lineHeight={24}
                                color="#3D4244"
                                className="w-[149px] text-center"
                            >
                                커미션
                            </DemiLightText>
                            <DemiLightText
                                fontSize={16}
                                lineHeight={24}
                                color="#3D4244"
                                className="w-[123px] text-center"
                            >
                                수업기록
                            </DemiLightText>
                            <DemiLightText
                                fontSize={16}
                                lineHeight={24}
                                color="#3D4244"
                                className="w-[127px] text-center"
                            >
                                수강권
                            </DemiLightText>
                        </Row>
                    ) : (
                        <Row className="flex flex-row w-full h-[44px] bg-[#EEF0F2] rounded-[9px] items-center">
                            <DemiLightText
                                fontSize={16}
                                lineHeight={24}
                                color="#3D4244"
                                className="w-[149px] text-center"
                            >
                                강사
                            </DemiLightText>
                            <DemiLightText
                                fontSize={16}
                                lineHeight={24}
                                color="#3D4244"
                                className="w-[145px] text-center"
                            >
                                회원
                            </DemiLightText>
                            <DemiLightText
                                fontSize={16}
                                lineHeight={24}
                                color="#3D4244"
                                className="w-[133px] text-center"
                            >
                                상태
                            </DemiLightText>
                            <DemiLightText
                                fontSize={16}
                                lineHeight={24}
                                color="#3D4244"
                                className="w-[219px] text-center"
                            >
                                수업 일시
                            </DemiLightText>
                            <DemiLightText
                                fontSize={16}
                                lineHeight={24}
                                color="#3D4244"
                                className="w-[226px] text-center"
                            >
                                수강권
                            </DemiLightText>
                            <DemiLightText
                                fontSize={16}
                                lineHeight={24}
                                color="#3D4244"
                                className="w-[129px] text-center"
                            >
                                회차/총 회차
                            </DemiLightText>
                            <DemiLightText
                                fontSize={16}
                                lineHeight={24}
                                color="#3D4244"
                                className="w-[135px] text-center"
                            >
                                정산액
                            </DemiLightText>
                            <DemiLightText
                                fontSize={16}
                                lineHeight={24}
                                color="#3D4244"
                                className="w-[129px] text-center"
                            >
                                수업 금액
                            </DemiLightText>
                        </Row>
                    )} */}
                    <Col className="w-full  items-center h-[500px]">
                        <MediumText
                            fontSize={20}
                            lineHeight={29}
                            letterSpacing={-0.3}
                            color="#3D4244"
                            className="mb-[8px] mt-[100px]"
                        >
                            조회 결과가 없어요
                        </MediumText>
                        <RegularText fontSize={16} lineHeight={24} color="#6B7280">
                            입력한 정보가 올바른지 확인해주세요.
                        </RegularText>
                    </Col>
                </Col>
            )}
            </>
            )}
            {isHover && (
                <Col
                    className=" absolute py-[10px] px-[14px] rounded-[9px]  w-[200px] h-[98px] bg-[#36393E] justify-center"
                    style={{
                        top: `${position.top - 100}px`,
                        left: `${position.left + 75}px`,
                        zIndex: 40,
                    }}
                >
                    <DemiLightText fontSize={13} lineHeight={18} letterSpacing={-0.32} color="white">
                        수강권에 대한 정산 비율을 설정했을
                    </DemiLightText>
                    <Row className="w-full mb-[8px]">
                        <DemiLightText fontSize={13} lineHeight={18} letterSpacing={-0.32} color="white">
                            경우 [수강권 아이콘
                        </DemiLightText>
                        <SvgIcon name="SvgMembershipTicket" size={16} fill="#B4BCC4" className="mx-[4px]" />
                        <DemiLightText fontSize={13} lineHeight={18} letterSpacing={-0.32} color="white">
                            ] 이 표시돼요
                        </DemiLightText>
                    </Row>
                    <DemiLightText fontSize={13} lineHeight={18} letterSpacing={-0.32} color="white">
                        * 표시가 없는 경우는 강사 정산 비율입니다.
                    </DemiLightText>
                </Col>
            )}
        </div>
    );
};

export default ClassPaySettlementPage;

// 회원별 수업별 버튼 Row
const GroupButtonRow = ({
    groupType,
    onGroupTypeChange,
}: {
    groupType: 'class' | 'member';
    onGroupTypeChange: (type: 'class' | 'member') => void;
}) => {
    // 정산 모드(base/tier/sessionTier)와 무관하게 두 탭을 모두 노출한다.
    // tier에서 '수강권별'을 감췄던 것은 백엔드가 tier 대표 요율을 못 내리던 시절의 임시 조치였다.
    const group = [
        {
            title: '수강권별',
            value: 'member',
        },
        {
            title: '수업별',
            value: 'class',
        },
    ];

    return (
        <Row className=" mr-[16px]">
            {group.map((item) => {
                const isSelected = item.value === groupType;
                const TextComponent = isSelected ? MediumText : RegularText;
                const bgColor = isSelected ? '#F4F5F7' : 'white';

                return (
                    <Col
                        className="rounded-full w-[101px] h-[52px] justify-center items-center cursor-pointer"
                        key={item.value}
                        style={{ backgroundColor: bgColor }}
                        onClick={() => onGroupTypeChange(item.value as 'class' | 'member')}
                    >
                        <TextComponent fontSize={20} lineHeight={24} color="#3D4244">
                            {item.title}
                        </TextComponent>
                    </Col>
                );
            })}
        </Row>
    );
};

// 헤더 표
const TableHeader = ({
    summaryData,
    date,
    totalRemainingScheduleSession,
}: {
    summaryData: GetClassFeeSettlementSummaryData;
    date: string;
    totalRemainingScheduleSession?: number;
}) => {
    const totalScheduleFee = summaryData?.totalScheduleFee;
    const totalPricePerSchedule = summaryData?.totalPricePerSchedule;
    const totalRemainSession = summaryData?.totalRemainSession;
    const totalAttendanceScheduleCount = summaryData?.totalAttendanceScheduleCount;
    const totalNoshowScheduleCount = summaryData?.totalNoshowScheduleCount;
    const totalScheduleMemberCount = summaryData?.totalScheduleMemberCount;

    return (
        <div className="flex  overflow: visible">
            <table className=" min-w-full bg-white border border-solid border-gray-300 text-center mb-[66px]">
                <thead>
                    <tr className="bg-gray-100 border-solid border-b h-[40px] justify-center items-center">
                        <th className="w-[228px] h-full justify-center items-center content-center  border border-solid border-[#D9D9D9] bg-[#F0F2F3]">
                            <Row className="items-center justify-center">
                                <RegularText
                                    fontSize={16}
                                    lineHeight={24}
                                    color="#6B7280"
                                    className="mr-[5px]"
                                    letterSpacing={-0.32}
                                >
                                    {`${dayjs(date).month() + 1}월 현재 수업 정산액`}
                                </RegularText>
                                {/* TooltipIcon 추가 */}
                                <TooltipIcon
                                    title={`${dayjs(date).month() + 1}월 현재까지 (${dayjs(date).startOf('month').format('YY.MM.DD')} ~ ${dayjs().format('YY.MM.DD')}) 완료된 수업 데이터를 기준으로 계산된 정산금액`}
                                />
                            </Row>
                        </th>
                        <th className="w-[228px] h-full justify-center items-center content-center  border border-solid border-[#D9D9D9] bg-[#F0F2F3]">
                            <Row className="items-center justify-center">
                                <RegularText
                                    fontSize={16}
                                    lineHeight={24}
                                    color="#6B7280"
                                    className="mr-[5px]"
                                    letterSpacing={-0.32}
                                >
                                    총 수업 금액
                                </RegularText>
                                <TooltipIcon
                                    title={`강사의 1회당 수업 금액(서비스표 금액)을 기준으로 이번 달에 등록된\n모든 수업의 총합`}
                                />
                            </Row>
                        </th>
                        <th className="w-[228px] h-full justify-center items-center content-center  border border-solid border-[#D9D9D9] bg-[#F0F2F3]">
                            <Row className="items-center justify-center">
                                <RegularText
                                    fontSize={16}
                                    lineHeight={24}
                                    color="#6B7280"
                                    className="mr-[5px]"
                                    letterSpacing={-0.32}
                                >
                                    남은 수업 수
                                </RegularText>
                                <TooltipIcon
                                    title={`결제 시 등록한 총 수업 수에서 조회 월 말까지 진행된 수업 수를 뺀 남은 수업 수의 합계 (이번 달은 현재 시점 기준)`}
                                    subTitle="* 결제일이 조회 월 이후인 수강권은 포함되지 않고, 추가 결제·환불·양도로 바뀐 회차는 그 일이 일어난 달부터 반영되고, 지난 달 값은 그 달 말 기준으로 고정됩니다. 지난 달 조회 시 그 달 말에 유효했던 수강권은 지금 만료됐어도 포함됩니다."
                                />
                            </Row>
                        </th>
                        <th className="w-[228px] h-full justify-center items-center content-center  border border-solid border-[#D9D9D9] bg-[#F0F2F3]">
                            <RegularText fontSize={16} lineHeight={24} color="#6B7280" letterSpacing={-0.32}>
                                완료 수업/노쇼
                            </RegularText>
                        </th>
                        <th className="w-[228px] h-full justify-center items-center content-center  border border-solid border-[#D9D9D9] bg-[#F0F2F3]">
                            <Row className="items-center justify-center">
                                <RegularText
                                    fontSize={16}
                                    lineHeight={24}
                                    color="#6B7280"
                                    className="mr-[5px]"
                                    letterSpacing={-0.32}
                                >
                                    수업한 회원 수
                                </RegularText>
                                <TooltipIcon
                                    title={`강사가 이번 달 동안 실제로 수업을 진행한 회원의 고유 수`}
                                    subTitle="* 동일 회원이 여러 번 참여했더라도 한 번만 계산됩니다."
                                />
                            </Row>
                        </th>
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        {/* 수업료 */}
                        <td className=" py-[22px] border border-solid border-[#D9D9D9] ">
                            <RegularText fontSize={23} lineHeight={24} color="#3D4244">
                                {convertToKRW(totalScheduleFee)}
                            </RegularText>
                        </td>
                        {/* 총 등록단가 */}
                        <td className="py-[22px] border border-solid border-[#D9D9D9]">
                            <RegularText fontSize={23} lineHeight={24} color="#3D4244">
                                {convertToKRW(totalPricePerSchedule)}
                            </RegularText>
                        </td>
                        {/* 남은 수업수 */}
                        <td className="py-[22px] border border-solid border-[#D9D9D9]">
                            <RegularText fontSize={23} lineHeight={24} color="#3D4244">
                                {totalRemainingScheduleSession
                                    ? totalRemainingScheduleSession
                                    : totalRemainingScheduleSession === 0
                                      ? '0'
                                      : '계산중...'}
                            </RegularText>
                        </td>
                        {/* 완료수업수 / 노쇼 */}
                        <td className="py-[22px] border border-solid border-[#D9D9D9]">
                            <RegularText fontSize={23} lineHeight={24} color="#3D4244">
                                {totalAttendanceScheduleCount}/{totalNoshowScheduleCount}
                            </RegularText>
                        </td>
                        {/* 수업한 회원 수 */}
                        <td className="py-[22px] border border-solid border-[#D9D9D9]">
                            <RegularText fontSize={23} lineHeight={24} color="#3D4244">
                                {totalScheduleMemberCount}
                            </RegularText>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
    );
};
